import React, { useContext, useEffect, useState } from "react";
import Commonmodel from "../../Commonmodel/Commonmodel";
import "../MoComponent.css";
import { TextField } from "@mui/material";
// import FingerPrintImg from "../../../Images/fingerprint.svg";
import { CommonContext } from "../../../context/CommonContext";
import { authHttp } from "../../../services/commonHttpService";
import { apisPath, paths } from "../../../utils/path";
import { encryptTextApi, fetchAbdmAccessToken, formatAbhaInput } from "../../../helpers/commonHelper";
import { useNavigate } from "react-router-dom";

const OtpVerification = ({ open, onClose, onVerified }) => {
    const navigate = useNavigate();
    const { setIsLoading, setSnackState } = useContext(CommonContext);

    const [step, setStep] = useState(1); // Step 1 = enter mobile/abha, Step 2 = OTP
    const [searchType, setSearchType] = useState("mobile"); // 'mobile' or 'abha'
    const [inputValue, setInputValue] = useState("");
    const [otp, setOtp] = useState("");
    const [abdmAccessToken, setAbdmAccessToken] = useState(null);
    const [txnId, setTxnId] = useState(null);
    const [canClose, setCanClose] = useState(false); // Control modal closing

    // Fetch ABDM Access Token when modal opens
    useEffect(() => {
        if (open) {
            loadAbdmToken();
            setCanClose(false); // Prevent closing when modal opens
        }

        // Cleanup: Clear token when modal closes
        return () => {
            if (!open) {
                setAbdmAccessToken(null);
                resetState();
            }
        };
    }, [open]);

    const loadAbdmToken = async () => {
        const token = await fetchAbdmAccessToken(setIsLoading, setSnackState);
        if (token) {
            setAbdmAccessToken(token);
        }
    };

    // Reset all state
    const resetState = () => {
        setStep(1);
        setInputValue("");
        setOtp("");
        setTxnId(null);
        setSearchType("mobile");
        setCanClose(false);
    };

    // Format OTP input
    const handleOtpChange = (e, setFunction) => {
        let value = e.target.value.replace(/\D/g, ""); // Only digits
        if (value.length > 6) value = value.slice(0, 6);
        setFunction(value);
    };

    // ----------------------- STEP 1 → SEND OTP -----------------------
    const handleSendOtp = async () => {
        if (!inputValue.trim()) return;

        try {
            setIsLoading(true);

            // Encrypt the input value (mobile or ABHA)
            const encryptedValue = await encryptTextApi(inputValue.trim());

            // Choose API endpoint based on search type
            const apiEndpoint = searchType === "mobile"
                ? apisPath?.sendMobileOtpMo
                : apisPath?.sendAbhaOtp;

            const response = await authHttp.post(
                apiEndpoint,
                { loginId: encryptedValue },
                {
                    headers: {
                        accessToken: abdmAccessToken,
                    }
                }
            );

            if (response?.data?.success) {
                const responseData = response?.data?.data;

                if (responseData?.txnId) {
                    setTxnId(responseData?.txnId);
                }

                setSnackState({
                    open: true,
                    message: responseData?.message || 'OTP sent successfully',
                    severity: 'success',
                });

                setStep(2); // Move to OTP verification step
            } else {
                throw new Error(response.data?.message || 'Failed to send OTP');
            }
        } catch (error) {
            setSnackState({
                open: true,
                message: error?.response?.data?.message || 'Failed to send OTP',
                severity: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // ----------------------- VERIFY OTP -----------------------
    const handleVerifyOtp = async () => {
        if (otp.length !== 6) return;

        try {
            setIsLoading(true);

            // Encrypt OTP
            const encryptedOtp = await encryptTextApi(otp);

            // Choose API endpoint based on search type
            const apiEndpoint = searchType === "mobile"
                ? apisPath?.verifyMobileOtpMo
                : apisPath?.verifyAbhaOtp;

            const response = await authHttp.post(
                apiEndpoint,
                {
                    txnId: txnId,
                    otpValue: encryptedOtp,
                },
                {
                    headers: {
                        accessToken: abdmAccessToken,
                    }
                }
            );

            if (response?.data?.success) {
                setSnackState({
                    open: true,
                    message: response?.data?.message || 'OTP verified successfully',
                    severity: 'success',
                });

                // Reset state and close modal
                resetState();
                onClose();

                // ✅ Call parent callback (parent will handle navigation)
                onVerified();
            }
        } catch (error) {
            setSnackState({
                open: true,
                message: error?.response?.data?.message || 'Failed to verify OTP',
                severity: 'error',
            });

            // Allow closing after failed verification
            setCanClose(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        resetState();
        onClose();
        // Navigate to beneficiary management
        if (!canClose) {
            navigate(`${paths?.user?.commonPoint}${paths?.user?.moBeneficiaryManagement}`);
        }
    };

    // Validation
    const isInputValid = searchType === "mobile"
        ? /^[6-9]\d{9}$/.test(inputValue) // 10 digits starting with 6-9
        : inputValue.length === 17 && inputValue.replace(/\D/g, '').length === 14; // ABHA ID: XX-XXXX-XXXX-XXXX

    return (
        <Commonmodel
            open={open}
            onClose={handleClose}
            title="To Start Treatment"
            showActions={false}
            disableBackdropClick={true}  // Modal won't close on outside click
        >
            <div className="otp-wrapper">
                {/* --------------------------- STEP 1 --------------------------- */}
                {step === 1 && (
                    <>
                        <p className="text-muted text-left small custom-pc">
                            Enter Mobile Number or ABHA ID to continue
                        </p>

                        <div className="search-type-box">
                            <label>
                                <input
                                    type="radio"
                                    value="mobile"
                                    checked={searchType === "mobile"}
                                    onChange={() => {
                                        setSearchType("mobile");
                                        setInputValue("");
                                    }}
                                />
                                Mobile Number
                            </label>

                            <label className="mx-4">
                                <input
                                    type="radio"
                                    value="abha"
                                    checked={searchType === "abha"}
                                    onChange={() => {
                                        setSearchType("abha");
                                        setInputValue("");
                                    }}
                                />
                                ABHA Number
                            </label>
                        </div>

                        <TextField
                            fullWidth
                            variant="standard"
                            label={
                                searchType === "mobile"
                                    ? "Enter Mobile Number"
                                    : "Enter ABHA Number"
                            }
                            placeholder={
                                searchType === "mobile"
                                    ? ""
                                    : "XX-XXXX-XXXX-XXXX"
                            }
                            type={searchType === "mobile" ? "tel" : "text"}
                            value={inputValue}

                            onChange={(e) => {
                                if (searchType === "mobile") {
                                    // Only allow digits, max 10
                                    let value = e.target.value.replace(/\D/g, "");
                                    if (value.length <= 10) {
                                        setInputValue(value);
                                    }
                                } else {
                                    // ABHA ID - auto-format with hyphens
                                    const formatted = formatAbhaInput(e.target.value);
                                    setInputValue(formatted);
                                }
                            }}

                            inputProps={{
                                maxLength: searchType === "mobile" ? 10 : 17,
                                style: { fontSize: "1rem" },
                            }}
                            error={inputValue.length > 0 && !isInputValid}
                            helperText={
                                inputValue.length > 0 && !isInputValid
                                    ? searchType === "mobile"
                                        ? "Please enter valid 10-digit mobile number"
                                        : "Please enter valid 14-digit ABHA ID"
                                    : ""
                            }
                            sx={{ mt: 2, mb: 2 }}
                        />

                        <button
                            className={`primary-btn ${isInputValid ? "active" : ""}`}
                            disabled={!isInputValid}
                            onClick={handleSendOtp}
                        >
                            Send OTP
                        </button>

                        {/* -------- OR Divider -------- */}
                        <div className="or-divider">
                            <span>OR</span>
                        </div>

                        {/* -------- Biometric Button -------- */}
                        <button className="biometric-btn d-flex justify-content-center mt-3">
                            {/* <img
                                src={FingerPrintImg} // replace with your correct icon path
                                alt="Biometric Icon"
                                className="bio-icon"
                            /> */}
                            Biometric
                        </button>
                    </>
                )}

                {/* --------------------------- STEP 2 --------------------------- */}
                {step === 2 && (
                    <>
                        <h5 className="text-center mb-2">OTP Verification</h5>
                        <p className="text-muted text-center small">
                            Enter the 6-digit OTP sent to your registered {searchType === "mobile" ? "mobile number" : "ABHA ID"}
                        </p>

                        <div className="otp-container">
                            <input
                                type="text"
                                value={otp}
                                onChange={(e) => handleOtpChange(e, setOtp)}
                                maxLength={6}
                                className="otp-input"
                                placeholder="_ _ _  -   _ _ _"
                            />
                        </div>

                        <button
                            className={`primary-btn ${otp.length === 6 ? "active" : ""}`}
                            disabled={otp.length !== 6}
                            onClick={handleVerifyOtp}
                        >
                            Verify OTP
                        </button>

                        <p className="resend-text">Resend OTP in 60s</p>
                    </>
                )}
            </div>
        </Commonmodel>
    );
}

export default OtpVerification;
